import {
  Box,
  useTheme,
  Typography,
  Link,
  Collapse,
  IconButton,
} from '@mui/material';
import { tokens } from '../../../global/theme/tokens';
import ExoAvatar from '../ExoAvatar';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { hasPermissions } from '../../../auth/hasPermissions';
import { useEmailClient } from '../../email-system/useEmailClient';
import { NewEmail } from '../../new/NewEmail';
import ExoDialog from '../ExoDialog';
import FeedbackButton from '../../special/FeedbackButton';

const ProjectParticipantCard = ({
  participantData,
  onRemove,
  noBackground,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const { emailCredentials } = useEmailClient();
  const navigate = useNavigate();

  const participant = participantData.participant;
  const type = participantData.type;
  const isCompanyType = type === 'companyLead' || type === 'company';
  const isInternalType = type === 'internal';

  const routeMap = {
    companyLead: '/leads/companies',
    personLead: '/leads/persons',
    company: '/companies',
    contact: '/contacts',
  };
  const route = routeMap[type] || '/contacts';

  const useInternalEmail =
    hasPermissions('email-inbox') && emailCredentials?.data?.length > 0;

  const [expanded, setExpanded] = useState(false);

  const [startData, setStartData] = useState(null);
  function handleEmailSelect(email) {
    setStartData({
      to: [
        {
          type,
          email,
          participant: participant,
        },
      ],
    });
  }

  const emails = participant?.emails || [];
  const phones = participant?.telephoneNumbers || [];

  const primaryEmail =
    emails.find((e) => e.isPrimary) || (emails.length > 0 ? emails[0] : null);
  const primaryPhone = phones.length > 0 ? phones[0] : null;

  const additionalEmails = emails.filter((e) => e.id !== primaryEmail?.id);
  const additionalPhones = phones.filter((p) => p.id !== primaryPhone?.id);
  const hasAdditional =
    additionalEmails.length > 0 || additionalPhones.length > 0;

  const displayName =
    isCompanyType || isInternalType
      ? participant.name
      : `${participant?.firstName} ${participant?.lastName}`;

  return (
    <Box
      className="flex flex-col w-full rounded-lg"
      sx={{
        bgcolor: noBackground ? 'transparent' : colors.bgInput,
        transition: 'background-color 0.15s ease',
        ':hover': {
          bgcolor: colors.card,
        },
      }}
    >
      {/* Header row */}
      <Box className="flex items-center gap-2 p-2 pb-0">
        <ExoAvatar
          picture={participant.logo ? participant.logo.temporaryUrl : ''}
          name={displayName}
          icon={participant?.icon}
          size="32"
          onClick={() =>
            !isInternalType && navigate(`${route}/${participant.id}`)
          }
        />
        <Box className="flex-1 min-w-0">
          <Typography fontSize="13px" fontWeight={600} noWrap>
            {displayName}
          </Typography>
          {participant?.position && (
            <Typography fontSize="11px" noWrap sx={{ opacity: 0.5 }}>
              {participant.position}
            </Typography>
          )}
        </Box>
        {onRemove && (
          <IconButton
            size="small"
            onClick={onRemove}
            sx={{
              opacity: 0,
              transition: 'opacity 0.15s ease, color 0.15s ease',
              '.MuiBox-root:hover > .MuiBox-root > &': { opacity: 0.5 },
              ':hover': {
                opacity: '1!important',
                color: colors.redAccent[500],
              },
              p: '3px',
            }}
          >
            <CloseIcon sx={{ fontSize: '14px' }} />
          </IconButton>
        )}
      </Box>

      {/* Contact rows */}
      <Box className="flex flex-col gap-0 px-2 pb-2 pt-1 ml-[40px]">
        {primaryPhone && (
          <ContactRow
            icon={<PhoneIcon sx={{ fontSize: '14px' }} />}
            label={primaryPhone.phoneNumber}
            href={`tel:${primaryPhone.phoneNumber}`}
            colors={colors}
          />
        )}
        {primaryEmail && (
          <ContactRow
            icon={<EmailIcon sx={{ fontSize: '14px' }} />}
            label={primaryEmail.emailAddress}
            isPrimary={primaryEmail.isPrimary}
            href={
              useInternalEmail
                ? undefined
                : `mailto:${primaryEmail.emailAddress}`
            }
            onClick={
              useInternalEmail
                ? () => handleEmailSelect(primaryEmail.emailAddress)
                : undefined
            }
            colors={colors}
          />
        )}

        {hasAdditional && (
          <>
            <Box
              className="flex items-center cursor-pointer mt-1"
              onClick={() => setExpanded(!expanded)}
              sx={{ opacity: 0.4, ':hover': { opacity: 0.8 } }}
            >
              <ExpandMoreIcon
                sx={{
                  fontSize: '16px',
                  transition: 'transform 0.2s ease',
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
              <Typography fontSize="10px" fontWeight={500} sx={{ ml: '2px' }}>
                {additionalPhones.length + additionalEmails.length} {t('more')}
              </Typography>
            </Box>
            <Collapse in={expanded}>
              <Box className="flex flex-col gap-0 mt-1">
                {additionalPhones.map((phone) => (
                  <ContactRow
                    key={phone.id}
                    icon={<PhoneIcon sx={{ fontSize: '14px' }} />}
                    label={phone.phoneNumber}
                    subLabel={phone.phoneType}
                    href={`tel:${phone.phoneNumber}`}
                    colors={colors}
                  />
                ))}
                {additionalEmails.map((email) => (
                  <ContactRow
                    key={email.id}
                    icon={<EmailIcon sx={{ fontSize: '14px' }} />}
                    label={email.emailAddress}
                    subLabel={email.emailType}
                    isPrimary={email.isPrimary}
                    href={
                      useInternalEmail
                        ? undefined
                        : `mailto:${email.emailAddress}`
                    }
                    onClick={
                      useInternalEmail
                        ? () => handleEmailSelect(email.emailAddress)
                        : undefined
                    }
                    colors={colors}
                  />
                ))}
              </Box>
            </Collapse>
          </>
        )}
      </Box>

      <ExoDialog open={Boolean(startData)} onClose={() => setStartData(null)}>
        <NewEmail startData={startData} onNewMail={() => setStartData(null)} />
      </ExoDialog>
    </Box>
  );
};

export default ProjectParticipantCard;

const ContactRow = ({
  icon,
  label,
  subLabel,
  href,
  onClick,
  isPrimary,
  colors,
}) => {
  const [copySuccess, setCopySuccess] = useState(null);

  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(label);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(null), 2000);
    } catch {
      setCopySuccess(false);
      setTimeout(() => setCopySuccess(null), 2000);
    }
  };

  const row = (
    <Box
      className="flex items-center min-w-0 rounded-sm group"
      onClick={onClick}
      sx={{
        py: '2px',
        px: '4px',
        cursor: 'pointer',
        transition: 'background-color 0.1s ease',
        ':hover': { bgcolor: colors.selected },
      }}
    >
      <Box
        sx={{ color: colors.info, display: 'flex', flexShrink: 0, mr: '6px' }}
      >
        {icon}
      </Box>
      {isPrimary && (
        <StarIcon
          sx={{
            fontSize: '12px',
            color: colors.warning,
            flexShrink: 0,
            mr: '4px',
          }}
        />
      )}
      {subLabel && (
        <Typography
          fontSize="10px"
          fontWeight={600}
          sx={{ flexShrink: 0, mr: '4px', opacity: 0.6 }}
        >
          {subLabel}
        </Typography>
      )}
      <Typography
        fontSize="11px"
        noWrap
        sx={{ flex: 1, minWidth: 0, opacity: 0.8 }}
      >
        {label}
      </Typography>
      <FeedbackButton
        onClick={handleCopy}
        success={copySuccess}
        className="min-w-0"
        sx={{
          flexShrink: 0,
          p: '2px',
          opacity: 0,
          transition: 'opacity 0.15s ease',
          '.group:hover &': { opacity: 0.5 },
          ':hover': { opacity: '1!important' },
        }}
      >
        {copySuccess === null && <ContentCopyIcon sx={{ fontSize: '12px' }} />}
      </FeedbackButton>
    </Box>
  );

  if (href) {
    return (
      <Link href={href} underline="none" color="inherit">
        {row}
      </Link>
    );
  }

  return row;
};
